import { Add as AddIcon } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fieldsApi, FieldSummary, LayerInfo } from '../../api/fields';
import { layersApi } from '../../api/layers';
import { layerTemplatesApi } from '../../api/layerTemplates';
import { ScenarioLayer, ScenarioLayerCreate, ScenarioLayerPropertyCreate, scenarioLayersApi } from '../../api/scenarioLayers';
import { LayerPropertiesViewMode } from '../../data/layerAttributeCategories';
import { getLayerTypeLabel, LayerTemplate, LayerTemplateDetail } from '../../types/layerTemplate';
import { ScenarioLayerPropertiesEditByCategory } from './ScenarioLayerPropertiesEditByCategory';
import { VolumetricEstimateDialog } from './VolumetricEstimateDialog';
import { VolumetricEstimateDraft } from '../../utils/volumetricEstimation';

const PROPERTY_DECIMAL_PLACES = 3;
const TEMPLATE_SEARCH_LIMIT = 50;
const TEMPLATE_SEARCH_DEBOUNCE_MS = 300;

interface LayerAttribute {
  id: string;
  name: string;
  unit?: string | null;
  type: string;
  description: string | null;
}

interface LayerPropertyForm {
  attribute_id: string;
  attribute_name: string;
  attribute_unit?: string | null;
  attribute_type: string;
  string_value?: string;
  float_value?: number;
  integer_value?: number;
  _inputString?: string; // For intermediate input states
}

interface CreateScenarioLayerModalProps {
  open: boolean;
  onClose: () => void;
  onLayerCreated: (layerData: ScenarioLayerCreate) => Promise<ScenarioLayer | void>;
  onLayerUpdated?: (scenarioLayerId: string, layerData: Partial<ScenarioLayerCreate>) => Promise<void>;
  scenarioId: string;
  editingLayer?: ScenarioLayer | null;
}

export const CreateScenarioLayerModal: React.FC<CreateScenarioLayerModalProps> = ({
  open,
  onClose,
  onLayerCreated,
  onLayerUpdated,
  scenarioId,
  editingLayer,
}) => {
  // Form state
  const [layerName, setLayerName] = useState('');
  const [creationType, setCreationType] = useState<'new' | 'existing' | 'template'>('new');
  const [selectedFieldId, setSelectedFieldId] = useState<string>('');
  const [selectedLayerId, setSelectedLayerId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Data state
  const [fields, setFields] = useState<FieldSummary[]>([]);
  const [fieldLayers, setFieldLayers] = useState<LayerInfo[]>([]);
  const [templates, setTemplates] = useState<LayerTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSearchInput, setTemplateSearchInput] = useState('');
  const templateSearchTimerRef = useRef<number | null>(null);
  const [layerAttributes, setLayerAttributes] = useState<LayerAttribute[]>([]);
  const [layerProperties, setLayerProperties] = useState<LayerPropertyForm[]>([]);
  const [tempLayerProperties, setTempLayerProperties] = useState<LayerPropertyForm[]>([]);
  
  // Resource state
  const [initialGeological, setInitialGeological] = useState<number | undefined>(undefined);
  const [initialGeologicalDraft, setInitialGeologicalDraft] = useState('');
  const [volumetricOpen, setVolumetricOpen] = useState(false);
  const [volumetricDraft, setVolumetricDraft] = useState<VolumetricEstimateDraft | null>(null);

  // State to track if layer is saved and source has been changed
  const [isLayerSaved, setIsLayerSaved] = useState(false);
  const [hasSourceChanged, setHasSourceChanged] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [propertiesViewMode, setPropertiesViewMode] = useState<LayerPropertiesViewMode>('light');

  const formatResourceValue = (value: number | undefined): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return '';
    return value.toFixed(PROPERTY_DECIMAL_PLACES);
  };

  const decimalRegex = new RegExp(`^-?\\d*(\\.\\d{0,${PROPERTY_DECIMAL_PLACES}})?$`);
  const partialDecimalRegex = /^\.?$|^-?\d+\.$/;

  const parseResourceDraftNumber = (raw: string): number | undefined => {
    if (raw === '' || raw === '-' || raw === '.' || raw === '-.' || partialDecimalRegex.test(raw)) {
      return undefined;
    }
    const value = parseFloat(raw);
    return Number.isNaN(value) ? undefined : value;
  };

  // Initialize form when editing layer
  useEffect(() => {
    if (editingLayer && open) {
      setLayerName(editingLayer.layer_name);
      setIsLayerSaved(true); // Layer is already saved
      setHasSourceChanged(false);

      // Load resource data if exists
      if (editingLayer.resource) {
        setInitialGeological(editingLayer.resource.initial_geological ?? undefined);
        setInitialGeologicalDraft(formatResourceValue(editingLayer.resource.initial_geological ?? undefined));
      } else {
        setInitialGeological(undefined);
        setInitialGeologicalDraft('');
      }
      setVolumetricDraft(null);

      // Determine creation type based on editing layer
      if (editingLayer.ref_field && editingLayer.ref_layer) {
        setCreationType('existing');
        setSelectedFieldId(editingLayer.ref_field);
        setSelectedLayerId(editingLayer.ref_layer);
        // Load field layers for the selected field
        loadFieldLayers(editingLayer.ref_field);
      } else if (editingLayer.ref_layer_template) {
        setCreationType('template');
        setSelectedTemplateId(editingLayer.ref_layer_template);
      } else {
        setCreationType('new');
      }

      // Initialize properties with saved values from scenario_layer_properties
      // Show ALL attributes, fill only those that were saved
      const initialProperties: LayerPropertyForm[] = layerAttributes.map(attr => {
        const existingProp = editingLayer.properties.find(prop => prop.attribute_id === attr.id);
        return {
          attribute_id: attr.id,
          attribute_name: attr.name,
          attribute_unit: attr.unit,
          attribute_type: attr.type,
          string_value: existingProp?.string_value || undefined,
          float_value: existingProp?.float_value || undefined,
          integer_value: existingProp?.integer_value || undefined,
        };
      });
      setLayerProperties(initialProperties);
      setTempLayerProperties([]); // Clear temp properties
    } else if (!editingLayer && open) {
      // Reset form for new layer
      setLayerName('');
      setCreationType('new');
      setSelectedFieldId('');
      setSelectedLayerId('');
      setSelectedTemplateId('');
      setFieldLayers([]);
      setError(null);
      setIsLayerSaved(false); // New layer is not saved yet
      setHasSourceChanged(false);

      // Clear all properties for new layer
      const emptyProperties: LayerPropertyForm[] = layerAttributes.map(attr => ({
        attribute_id: attr.id,
        attribute_name: attr.name,
        attribute_unit: attr.unit,
        attribute_type: attr.type,
        string_value: undefined,
        float_value: undefined,
        integer_value: undefined,
      }));
      setLayerProperties(emptyProperties);
      setTempLayerProperties([]);
      setInitialGeologicalDraft('');
      setVolumetricDraft(null);
    }
  }, [editingLayer, open, layerAttributes]);

  const formatTemplateLabel = useCallback((template: LayerTemplate): string => {
    const typeLabel = template.type ? getLayerTypeLabel(template.type) : 'No type';
    return `${template.name} (${typeLabel})`;
  }, []);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId]
  );

  // Load initial data
  useEffect(() => {
    if (open) {
      loadInitialData();
    }
  }, [open]);

  // Load fields only when existing-reservoir mode is used
  useEffect(() => {
    if (open && creationType === 'existing') {
      loadFields();
    }
  }, [open, creationType]);

  // Lazy-load template options only when template mode is active
  useEffect(() => {
    if (!open || creationType !== 'template') {
      return;
    }
    loadTemplates('', editingLayer?.ref_layer_template || undefined);
  }, [open, creationType, editingLayer?.ref_layer_template]);

  // Load field layers when field is selected
  useEffect(() => {
    if (selectedFieldId && creationType === 'existing') {
      loadFieldLayers(selectedFieldId);
    }
  }, [selectedFieldId, creationType]);

  // Handle creation type change
  const handleCreationTypeChange = (newType: 'new' | 'existing' | 'template') => {
    // Prevent switching to 'existing' type
    if (newType === 'existing') {
      return;
    }
    setCreationType(newType);
    setSelectedFieldId('');
    setSelectedLayerId('');
    setSelectedTemplateId('');
    setFieldLayers([]);

    // Clear properties and load from new source
    clearAndLoadFromSource(newType);
  };

  // Handle template selection change
  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (templateId) {
      clearAndLoadFromSource('template', templateId);
    }
  };

  // Handle layer selection change
  const handleLayerChange = (layerId: string) => {
    setSelectedLayerId(layerId);
    if (layerId) {
      clearAndLoadFromSource('existing', undefined, layerId);
    }
  };

  // Clear properties and load from source
  const clearAndLoadFromSource = async (sourceType: 'new' | 'existing' | 'template', templateId?: string, layerId?: string) => {
    // Clear all properties first
    const emptyProperties: LayerPropertyForm[] = layerAttributes.map(attr => ({
      attribute_id: attr.id,
      attribute_name: attr.name,
      attribute_unit: attr.unit,
      attribute_type: attr.type,
      string_value: undefined,
      float_value: undefined,
      integer_value: undefined,
    }));

    if (isLayerSaved) {
      // For saved layers, use temp properties when source changes
      setTempLayerProperties(emptyProperties);
      setHasSourceChanged(true);
    } else {
      // For new layers, update main properties directly
      setLayerProperties(emptyProperties);
      setTempLayerProperties([]);
    }

    // Load properties from source
    if (sourceType === 'template' && templateId) {
      await loadTemplateProperties(templateId);
    } else if (sourceType === 'existing' && layerId) {
      await loadLayerProperties(layerId);
    }
    // For 'new' type, properties remain empty
  };

  // Load template properties when template is selected (but not during initial load of editing layer)
  useEffect(() => {
    if (selectedTemplateId && creationType === 'template' && !editingLayer) {
      loadTemplateProperties(selectedTemplateId);
    }
  }, [selectedTemplateId, creationType, editingLayer]);

  // Load layer properties when existing layer is selected (but not during initial load of editing layer)
  useEffect(() => {
    if (selectedLayerId && creationType === 'existing' && !editingLayer) {
      loadLayerProperties(selectedLayerId);
    }
  }, [selectedLayerId, creationType, editingLayer]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const attributes = await layersApi.getLayerAttributes();
      setLayerAttributes(attributes);

      // Initialize properties form with all attributes
      const initialProperties: LayerPropertyForm[] = attributes.map(attr => ({
        attribute_id: attr.id,
        attribute_name: attr.name,
        attribute_type: attr.type,
        string_value: undefined,
        float_value: undefined,
        integer_value: undefined,
      }));
      setLayerProperties(initialProperties);
    } catch (err) {
      console.error('Failed to load layer attributes:', err);
      setError('Failed to load layer attributes');
    } finally {
      setLoading(false);
    }
  };

  const loadFields = async () => {
    try {
      const fieldsData = await fieldsApi.getFieldsList();
      setFields(fieldsData.fields);
    } catch (err) {
      console.error('Failed to load fields:', err);
      setError('Failed to load fields');
    }
  };

  const loadTemplates = async (search: string, ensureTemplateId?: string) => {
    try {
      setTemplatesLoading(true);
      const templatesData = await layerTemplatesApi.getTemplatesList({
        search: search.trim() || undefined,
        limit: TEMPLATE_SEARCH_LIMIT,
        include_user_names: false,
      });

      let nextTemplates = templatesData.templates;
      if (ensureTemplateId && !nextTemplates.some((template) => template.id === ensureTemplateId)) {
        try {
          const ensuredTemplate = await layerTemplatesApi.getTemplate(ensureTemplateId);
          nextTemplates = [ensuredTemplate, ...nextTemplates];
        } catch (ensureError) {
          console.warn('Failed to preload selected template option:', ensureError);
        }
      }

      setTemplates(nextTemplates);
    } catch (err) {
      console.error('Failed to load layer templates:', err);
      setError('Failed to load layer templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const scheduleTemplateSearch = (search: string) => {
    if (templateSearchTimerRef.current !== null) {
      window.clearTimeout(templateSearchTimerRef.current);
    }
    templateSearchTimerRef.current = window.setTimeout(() => {
      loadTemplates(search, selectedTemplateId || undefined);
    }, TEMPLATE_SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => {
    return () => {
      if (templateSearchTimerRef.current !== null) {
        window.clearTimeout(templateSearchTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      setTemplateSearchInput(formatTemplateLabel(selectedTemplate));
    }
  }, [selectedTemplate, formatTemplateLabel]);

  const loadFieldLayers = async (fieldId: string) => {
    try {
      const fieldDetail = await fieldsApi.getFieldDetail(fieldId);
      setFieldLayers(fieldDetail.layers);
    } catch (err) {
      console.error('Failed to load field layers:', err);
      setError('Failed to load field layers');
    }
  };

  const loadTemplateProperties = async (templateId: string) => {
    try {
      const templateDetail: LayerTemplateDetail = await layerTemplatesApi.getTemplate(templateId);

      // Update properties with template values - show ALL attributes
      const updatedProperties = layerAttributes.map(attr => {
        const templateProp = templateDetail.properties.find(tp => tp.id === attr.id);
        return {
          attribute_id: attr.id,
          attribute_name: attr.name,
          attribute_unit: attr.unit,
          attribute_type: attr.type,
          string_value: templateProp?.type === 'string' ? (templateProp.value as string) : undefined,
          float_value: templateProp?.type === 'float' ? (templateProp.value as number) : undefined,
          integer_value: templateProp?.type === 'integer' ? (templateProp.value as number) : undefined,
        };
      });

      if (isLayerSaved && hasSourceChanged) {
        // For saved layers with changed source, update temp properties
        setTempLayerProperties(updatedProperties);
      } else {
        // For new layers or first load, update main properties
        setLayerProperties(updatedProperties);
      }
    } catch (err) {
      console.error('Failed to load template properties:', err);
      setError('Failed to load template properties');
    }
  };

  const loadLayerProperties = async (layerId: string) => {
    try {
      const layerDetail = await layersApi.getLayer(layerId);

      // Update properties with layer values - show ALL attributes
      const updatedProperties = layerAttributes.map(attr => {
        const layerProp = layerDetail.properties.find(lp => lp.attribute_id === attr.id);
        return {
          attribute_id: attr.id,
          attribute_name: attr.name,
          attribute_unit: attr.unit,
          attribute_type: attr.type,
          string_value: layerProp?.value_type === 'string' ? String(layerProp.value) : undefined,
          float_value: layerProp?.value_type === 'float' ? Number(layerProp.value) : undefined,
          integer_value: layerProp?.value_type === 'integer' ? Number(layerProp.value) : undefined,
        };
      });

      if (isLayerSaved && hasSourceChanged) {
        // For saved layers with changed source, update temp properties
        setTempLayerProperties(updatedProperties);
      } else {
        // For new layers or first load, update main properties
        setLayerProperties(updatedProperties);
      }
    } catch (err) {
      console.error('Failed to load layer properties:', err);
      setError('Failed to load layer properties');
    }
  };


  const handleSubmit = async () => {
    console.log('DEBUG: handleSubmit called');

    if (!layerName.trim()) {
      setError('Layer name is required');
      return;
    }

    if (creationType === 'existing' && !selectedLayerId) {
      setError('Please select an existing layer');
      return;
    }

    if (creationType === 'template' && !selectedTemplateId) {
      setError('Please select a layer template');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Prepare properties for submission
      // Use temporary properties if they exist (when source was changed during editing)
      const propertiesToUse = tempLayerProperties.length > 0 ? tempLayerProperties : layerProperties;
      console.log('DEBUG: propertiesToUse length:', propertiesToUse.length);

      const propertiesToSubmit: ScenarioLayerPropertyCreate[] = propertiesToUse
        .map(prop => ({
          attribute_id: prop.attribute_id,
          string_value: prop.string_value,
          float_value: prop.float_value,
          integer_value: prop.integer_value,
        }));

      console.log('DEBUG: propertiesToSubmit length:', propertiesToSubmit.length);

      const layerData: ScenarioLayerCreate = {
        scenario_id: scenarioId,
        layer_name: layerName.trim(),
        ref_field: creationType === 'existing' ? selectedFieldId : undefined,
        ref_layer: creationType === 'existing' ? selectedLayerId : undefined,
        ref_layer_template: creationType === 'template' ? selectedTemplateId : undefined,
        properties: propertiesToSubmit,
        resource: initialGeological !== undefined ? {
          initial_geological: initialGeological ?? null,
        } : null,
      };

      console.log('DEBUG: About to send layerData:', {
        scenario_id: layerData.scenario_id,
        layer_name: layerData.layer_name,
        ref_field: layerData.ref_field,
        ref_layer: layerData.ref_layer,
        ref_layer_template: layerData.ref_layer_template,
        properties_count: layerData.properties.length
      });

      let savedLayerId = editingLayer?.scenario_layer_id;

      if (editingLayer && onLayerUpdated) {
        console.log('DEBUG: Updating existing layer');
        await onLayerUpdated(editingLayer.scenario_layer_id, layerData);
      } else {
        console.log('DEBUG: Creating new layer');
        const created = await onLayerCreated(layerData);
        if (created?.scenario_layer_id) {
          savedLayerId = created.scenario_layer_id;
        }
      }

      if (savedLayerId && volumetricDraft?.total_geological_thousand_tons != null) {
        try {
          await scenarioLayersApi.saveVolumetricEstimate(savedLayerId, {
            area_km2: volumetricDraft.area_km2 ?? null,
            oil_density_g_cm3: volumetricDraft.oil_density_g_cm3 ?? null,
            points: volumetricDraft.points.map((p) => ({
              gross_thickness_m: p.gross_thickness_m ?? null,
              net_to_gross_pct: p.net_to_gross_pct ?? null,
              porosity_pct: p.porosity_pct ?? null,
              water_saturation_pct: p.water_saturation_pct ?? null,
              oil_fvf: p.oil_fvf ?? null,
            })),
            total_geological_thousand_tons: volumetricDraft.total_geological_thousand_tons,
          });
        } catch (volErr) {
          console.warn('Failed to persist volumetric estimate draft:', volErr);
        }
      }

      // Mark layer as saved and reset change tracking
      setIsLayerSaved(true);
      setHasSourceChanged(false);

      // Move temp properties to main properties if they exist
      if (tempLayerProperties.length > 0) {
        setLayerProperties(tempLayerProperties);
        setTempLayerProperties([]);
      }

      // Reset form and close modal on success
      setLayerName('');
      setCreationType('new');
      setSelectedFieldId('');
      setSelectedLayerId('');
      setSelectedTemplateId('');
      setFieldLayers([]);
      setTempLayerProperties([]);
      setInitialGeological(undefined);
      setInitialGeologicalDraft('');
      setVolumetricDraft(null);
      setError(null);
      // Small delay to ensure data is updated before closing modal
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (err: any) {
      console.error('Failed to create scenario layer:', err);
      setError(err.response?.data?.detail || 'Failed to create scenario layer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setLayerName('');
    setCreationType('new');
    setSelectedFieldId('');
    setSelectedLayerId('');
    setSelectedTemplateId('');
    setFieldLayers([]);
    setTemplates([]);
    setTemplateSearchInput('');
    setTempLayerProperties([]);
    setInitialGeological(undefined);
    setInitialGeologicalDraft('');
    setVolumetricDraft(null);
    setVolumetricOpen(false);
    setError(null);
    setIsLayerSaved(false);
    setHasSourceChanged(false);
    setPropertiesViewMode('light');
    onClose();
  };

  const handlePropertyChange = (attributeId: string, field: 'string_value' | 'float_value' | 'integer_value', value: string | number | undefined) => {
    // If we have temporary properties (source was changed), update them
    if (tempLayerProperties.length > 0) {
      setTempLayerProperties(prev =>
        prev.map(prop =>
          prop.attribute_id === attributeId
            ? { ...prop, [field]: value }
            : prop
        )
      );
    } else {
      // Otherwise update regular properties
      setLayerProperties(prev =>
        prev.map(prop =>
          prop.attribute_id === attributeId
            ? { ...prop, [field]: value }
            : prop
        )
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon />
          <Typography variant="h6">
            {editingLayer ? 'Edit Reservoir' : 'Add Reservoir to Scenario'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Layer Name */}
          <TextField
            label="Reservoir Name"
            value={layerName}
            onChange={(e) => setLayerName(e.target.value)}
            fullWidth
            required
            placeholder="Enter reservoir name"
          />

          {/* Creation Type */}
          <FormControl fullWidth>
            <InputLabel>Creation Type</InputLabel>
            <Select
              value={creationType}
              onChange={(e) => handleCreationTypeChange(e.target.value as 'new' | 'existing' | 'template')}
              label="Creation Type"
            >
              <MenuItem value="new">Create New Reservoir</MenuItem>
              {/* Hidden but not removed */}
              {false && (
              <MenuItem value="existing">Use Existing Reservoir</MenuItem>
              )}
              <MenuItem value="template">Use Reservoir Template</MenuItem>
            </Select>
          </FormControl>

          {/* Field Selection (for existing layers) */}
          {creationType === 'existing' && (
            <FormControl fullWidth>
              <InputLabel>Select Deposit (Field)</InputLabel>
              <Select
                value={selectedFieldId}
                onChange={(e) => {
                  setSelectedFieldId(e.target.value);
                  setSelectedLayerId(''); // Reset layer selection
                }}
                label="Select Deposit (Field)"
              >
                {fields.map((field) => (
                  <MenuItem key={field.id} value={field.id}>
                    {field.name} ({field.type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Layer Selection (for existing layers) */}
          {creationType === 'existing' && selectedFieldId && (
            <FormControl fullWidth>
              <InputLabel>Select Reservoir</InputLabel>
              <Select
                value={selectedLayerId}
                onChange={(e) => handleLayerChange(e.target.value)}
                label="Select Reservoir"
                disabled={fieldLayers.length === 0}
              >
                {fieldLayers.map((layer) => (
                  <MenuItem key={layer.id} value={layer.id}>
                    {layer.name} ({layer.type || 'No type'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Template Selection (for templates) */}
          {creationType === 'template' && (
            <Autocomplete
              options={templates}
              value={selectedTemplate}
              loading={templatesLoading}
              onChange={(_, option) => handleTemplateChange(option?.id ?? '')}
              onInputChange={(_, value, reason) => {
                setTemplateSearchInput(value);
                if (reason === 'input') {
                  scheduleTemplateSearch(value);
                }
                if (reason === 'clear') {
                  handleTemplateChange('');
                }
              }}
              inputValue={templateSearchInput}
              getOptionLabel={(option) => formatTemplateLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options) => options}
              noOptionsText={templatesLoading ? 'Loading templates...' : 'No templates found'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Reservoir Template"
                  placeholder="Type to search templates..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {templatesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          {/* Layer Properties */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Reservoir Properties
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={propertiesViewMode}
                onChange={(_, value: LayerPropertiesViewMode | null) => {
                  if (value) {
                    setPropertiesViewMode(value);
                  }
                }}
                aria-label="Properties view mode"
              >
                <ToggleButton value="light">Light</ToggleButton>
                <ToggleButton value="pro">Pro</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ maxHeight: propertiesViewMode === 'pro' ? 600 : undefined, overflow: propertiesViewMode === 'pro' ? 'auto' : undefined }}>
              <ScenarioLayerPropertiesEditByCategory
                properties={layerProperties}
                tempProperties={tempLayerProperties.length > 0 ? tempLayerProperties : undefined}
                onPropertyChange={handlePropertyChange}
                onTempPropertyUpdate={setTempLayerProperties}
                onPropertyUpdate={setLayerProperties}
                loading={loading}
                viewMode={propertiesViewMode}
              />
            </Box>
          </Box>

          {/* Resource Section */}
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Resource Data
              </Typography>
            </Divider>

            <Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', justifyContent: 'flex-start' }}>
                <TextField
                  size="small"
                  label="Initial Geological (thousand t)"
                  value={initialGeologicalDraft}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (!decimalRegex.test(raw)) return;
                    setInitialGeologicalDraft(raw);
                    setInitialGeological(parseResourceDraftNumber(raw));
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{
                    width: { xs: 220, sm: 260, md: 280 },
                    '& .MuiInputBase-root': { height: 40 },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => setVolumetricOpen(true)}
                  sx={{
                    height: 40,
                    minHeight: 40,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    px: 2,
                  }}
                >
                  Estimate
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1.75 }}>
                Initial geological oil in place (thousand t). Enter manually or use volumetric estimate.
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <VolumetricEstimateDialog
        open={volumetricOpen}
        onClose={() => setVolumetricOpen(false)}
        scenarioLayerId={editingLayer?.scenario_layer_id ?? null}
        layerProperties={
          (tempLayerProperties.length > 0 ? tempLayerProperties : layerProperties).map((p) => ({
            attribute_name: p.attribute_name,
            float_value: p.float_value,
            integer_value: p.integer_value,
            attribute_unit: p.attribute_unit,
          }))
        }
        draft={volumetricDraft}
        onDraftChange={setVolumetricDraft}
        onApply={(total, nextDraft) => {
          setInitialGeological(total);
          setInitialGeologicalDraft(formatResourceValue(total));
          setVolumetricDraft(nextDraft);
        }}
      />

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !layerName.trim()}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {isSubmitting ? (editingLayer ? 'Updating...' : 'Adding...') : (editingLayer ? 'Update Reservoir' : 'Add Reservoir')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
